import { faker } from '@faker-js/faker';

import {
  ACTIVE_CASE_STATUSES,
  ActiveCase,
  ENOPDonorType,
  NEW_CASE_STATUSES,
  TransportName,
  type ENOPCaseStatus,
} from '#entities';
import { ECaseCardStatus, EMaoLegType, ENOPOrganType } from '#shared';

export const generateCaseLabel = (): string =>
  `DEMO${faker.string.numeric(3)}-${faker.helpers.arrayElement(['HT', 'LI', 'LU'])}`;

export const generateUnosDonorId = (): string =>
  faker.string.alpha({ length: 4, casing: 'upper' }) + faker.string.numeric(3);
export const generateCase = (type: 'new' | 'active' = 'active'): ActiveCase => {
  const isNewCase = type === 'new';
  const organTypes = ['Heart', 'Liver', 'Lungs'] as const;
  const donorTypes = [ENOPDonorType.DBD, ENOPDonorType.DCD] as const;
  const statuses = isNewCase ? NEW_CASE_STATUSES : ACTIVE_CASE_STATUSES;
  const cardStatuses = ['ok', 'warning', 'accept', 'error'] as const;
  const legTypes = Object.values(EMaoLegType);

  const organType = faker.helpers.arrayElement(organTypes) as ENOPOrganType;
  const organAbbrev = { Heart: 'HT', Liver: 'LI', Lungs: 'LU' }[organType];
  const donorType = faker.helpers.arrayElement(donorTypes);
  const status = faker.helpers.arrayElement(statuses);
  const cardStatus = faker.helpers.arrayElement(cardStatuses);

  const unosDonorId = generateUnosDonorId();
  const transplantCenterCode = faker.string.alpha({
    length: 4,
    casing: 'upper',
  });

  const progress = faker.number.int({ min: 0, max: 100 });
  const pointCount = faker.number.int({ min: 3, max: 4 });

  return {
    id: faker.string.uuid(),
    unosDonorId,
    flightIds: isNewCase
      ? []
      : Array.from(
          { length: faker.number.int({ min: 1, max: 3 }) },
          () => `N${faker.string.numeric(3)}TX`,
        ),
    organ: {
      id: `${faker.string.uuid()}-${organType}`,
      name: organType,
      startPoint: faker.number.int({ min: 80, max: 100 }),
    },
    label: generateCaseLabel(),
    donorType,
    showOnMap: true,
    transplantCenterCode,
    etaString: `${faker.date.future().toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/New_York',
    })} EDT`,
    etaUtc: new Date().toISOString(),
    donorORTimeString: `${faker.date.recent().toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/New_York',
    })} EDT`,
    caseTimerEndString: `+${faker.string.numeric(2)}:${faker.string.numeric(2)}`,
    showOrgan: faker.datatype.boolean(),
    showReturnToBaseState: faker.datatype.boolean(),
    showDonorOr: faker.datatype.boolean(),
    showTimer: faker.datatype.boolean(),
    showOrganOnCard: faker.datatype.boolean(),
    showTransportOnCard: faker.datatype.boolean(),
    donorORDateTimeUtc: faker.date.soon().toISOString(),
    status,
    cardStatus: cardStatus as ECaseCardStatus,
    points: isNewCase
      ? []
      : Array.from({ length: pointCount }, (_, index) => ({
          id: faker.string.uuid(),
          point: Math.round((index / (pointCount - 1)) * 100),
          type: faker.helpers.arrayElement([
            'airMao',
            'donorOR',
            'donorOrAddress',
            'groundOnly',
            'organ',
          ]),
          hidden: faker.datatype.boolean(),
          subId: faker.datatype.boolean()
            ? `N${faker.string.numeric(3)}TX`
            : undefined,
        })),
    transport: isNewCase
      ? null
      : faker.datatype.boolean()
        ? (faker.helpers.arrayElement([
            'groundOnly',
            'airNonMao',
            'airMao',
          ]) as TransportName)
        : null,
    progress,
    mapProgress: progress + faker.number.int({ min: -10, max: 10 }),
    pathLabels: Array.from(
      { length: faker.number.int({ min: 3, max: 6 }) },
      (_, index) => ({
        id: faker.string.uuid(),
        name: faker.location.city(),
        startPoint: Math.round((index / 4) * 100),
        isActive: faker.datatype.boolean(),
      }),
    ),
    donorHospital: {
      location: faker.location.streetAddress(),
      address: `${faker.location.city()}, ${faker.location.state({ abbreviated: true })}`,
      state: faker.location.state({ abbreviated: true }),
      lonLat: [
        faker.location.longitude({ min: -125, max: -65 }),
        faker.location.latitude({ min: 25, max: 49 }),
      ],
      timezone: 'America/New_York',
      code: faker.string.alpha({ length: 8, casing: 'upper' }),
    },
    recipientHospital: {
      location: faker.location.streetAddress(),
      address: `${faker.location.city()}, ${faker.location.state({ abbreviated: true })}`,
      state: faker.location.state({ abbreviated: true }),
      lonLat: [
        faker.location.longitude({ min: -125, max: -65 }),
        faker.location.latitude({ min: 25, max: 49 }),
      ],
      timezone: 'America/New_York',
      code: transplantCenterCode,
    },
    trialProgram: null,
    logistics: null,
    serviceRegionId: undefined,
    trips: isNewCase
      ? []
      : Array.from({ length: faker.number.int({ min: 1, max: 3 }) }, () => {
          const tripStart = faker.date.recent();
          const tripEnd = faker.date.future();
          return {
            ExternalReference: `${unosDonorId}${organAbbrev} / ${donorType} ${organAbbrev} / ${faker.person.firstName()}`,
            TripStartUnix: tripStart.getTime(),
            TripEndUnix: tripEnd.getTime(),
            Id: faker.number.int({ min: 10000, max: 99999 }),
            Legs: Array.from(
              { length: faker.number.int({ min: 1, max: 3 }) },
              (_, index) => {
                const offBlocksUtc = faker.date.recent();
                const onBlocksUtc = faker.date.future();
                const scheduledOffBlocksUtc = faker.date.recent();
                const scheduledOnBlocksUtc = faker.date.future();
                return {
                  TripId: faker.number.int({ min: 10000, max: 99999 }),
                  OffBlocksUtc: offBlocksUtc.toISOString(),
                  OffBlocksUtcUnix: offBlocksUtc.getTime(),
                  OnBlocksUtc: onBlocksUtc.toISOString(),
                  OnBlocksUtcUnix: onBlocksUtc.getTime(),
                  LegType: faker.helpers.arrayElement(legTypes),
                  Id: faker.number.int({ min: 10000, max: 99999 }),
                  SectorIndex: index + 1,
                  DepartureAirport: {
                    ICAO: `K${faker.string.alpha({ length: 3, casing: 'upper' })}`,
                    IATA: faker.string.alpha({ length: 3, casing: 'upper' }),
                    ServedCity: faker.location.city(),
                    Latitude: faker.location.latitude({ min: 25, max: 49 }),
                    Longitude: faker.location.longitude({
                      min: -125,
                      max: -65,
                    }),
                    Id: faker.number.int({ min: 1000, max: 9999 }),
                    IanaTimeZone: 'America/New_York',
                    ServedCityArea: {
                      Code: faker.location.state({ abbreviated: true }),
                    },
                    Country: {
                      Name: 'UNITED STATES OF AMERICA',
                    },
                  },
                  ArrivalAirport: {
                    ICAO: `K${faker.string.alpha({ length: 3, casing: 'upper' })}`,
                    IATA: faker.string.alpha({ length: 3, casing: 'upper' }),
                    ServedCity: faker.location.city(),
                    Latitude: faker.location.latitude({ min: 25, max: 49 }),
                    Longitude: faker.location.longitude({
                      min: -125,
                      max: -65,
                    }),
                    Id: faker.number.int({ min: 1000, max: 9999 }),
                    IanaTimeZone: 'America/New_York',
                    ServedCityArea: {
                      Code: faker.location.state({ abbreviated: true }),
                    },
                    Country: {
                      Name: 'UNITED STATES OF AMERICA',
                    },
                  },
                  Movement: {
                    ScheduledOffBlocksUtc: scheduledOffBlocksUtc.toISOString(),
                    ScheduledOffBlocksUtcUnix: scheduledOffBlocksUtc.getTime(),
                    ScheduledOnBlocksUtc: scheduledOnBlocksUtc.toISOString(),
                    ScheduledOnBlocksUtcUnix: scheduledOnBlocksUtc.getTime(),
                    IsOffBlocks: faker.datatype.boolean(),
                    IsOnBlocks: faker.datatype.boolean(),
                    Aircraft: {
                      Registration: `N${faker.string.numeric(3)}TX`,
                      Id: faker.number.int({ min: 1000, max: 9999 }),
                    },
                  },
                };
              },
            ),
          };
        }),
  };
};

export const generateMockCases = (
  count: number,
  statusFilter?: ENOPCaseStatus[],
): ActiveCase[] => {
  const caseStatuses = statusFilter || ACTIVE_CASE_STATUSES;

  const donorTypes = Object.values(ENOPDonorType);
  const organTypes = ['HT', 'LI', 'LU', 'KI'];

  return Array.from({ length: count }, (_, index) => {
    const baseCase = generateCase();
    return {
      ...baseCase,
      id: `case-${index + 1}`,
      label: `WGT${String(index + 1).padStart(3, '0')}-${
        organTypes[index % organTypes.length]
      }`,
      status: caseStatuses[index % caseStatuses.length],
      donorType: donorTypes[index % donorTypes.length],
      donorORTimeString: `${String(Math.floor(Math.random() * 12) + 1).padStart(
        2,
        '0',
      )}:${String(Math.floor(Math.random() * 60)).padStart(2, '0')} ${
        Math.random() > 0.5 ? 'AM' : 'PM'
      } EDT`,
      transplantCenterCode: ['NYMS', 'CALI', 'FLOR', 'TEXA', 'MINN'][index % 5],
      etaUtc: new Date().toISOString(),
      etaString: `${String(Math.floor(Math.random() * 12) + 1).padStart(
        2,
        '0',
      )}:${String(Math.floor(Math.random() * 60)).padStart(2, '0')} EDT`,
    };
  });
};
